'use client';

/**
 * Hook for managing debug mode from URL parameters
 *
 * Usage:
 * - ?debug=1 or ?debug=true -> Enable debug mode
 * - ?debug=0 or ?debug=false -> Disable debug mode
 * - No param -> Use stored setting from localStorage
 *
 * Once set, the value persists in localStorage until explicitly changed.
 * URL parameter is removed after being read (via router.replace).
 */

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAtom } from 'jotai';
import { debugModeAtom } from '@sourcepilot/ui/shell/state/ui-atoms';
import { useSearchParams } from 'next/navigation';

export function useDebugMode() {
  const [debugMode, setDebugMode] = useAtom(debugModeAtom);
  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const debugParam = searchParams?.get('debug');

    if (debugParam !== null) {
      // URL param present - set debug mode based on value
      const isDebugMode = debugParam === '1' || debugParam === 'true';
      setDebugMode(isDebugMode);

      // Clean up URL by removing debug parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('debug');
      const newQueryString = newSearchParams.toString();
      const newPath = newQueryString
        ? `${window.location.pathname}?${newQueryString}`
        : window.location.pathname;

      router.replace(newPath);
    }
    // If no URL param, keep whatever is in localStorage (loaded by atomWithStorage)
  }, [searchParams, setDebugMode, router]);

  return { debugMode, setDebugMode };
}
